import { useInfiniteQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import toast from 'react-hot-toast';
import {
  generateCombinedFilterExpressionWithAnd,
  type FilterParameters,
  generateEqualExpression,
  generateIncludeQuery,
  generateSortQuery,
  generateURL,
  generateORExpression,
  generatePaginationQuery,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { ChatMessageResponse } from 'types/Chats/ChatMessage';
import { CACHE_AND_STALE_TIME } from 'constants/index';

interface PaginationOptions {
  pageSize: number;
  initialPage?: number;
}

export const getChatMessagesPage = (
  chatID: string | undefined,
  pageParam: number,
  pageSize: number
) => {
  // AuthorRoleID
  const orFilters: FilterParameters[] = [
    {
      type: 'equal',
      key: 'authorRoleID',
      value: 1,
    },
    {
      type: 'equal',
      key: 'authorRoleID',
      value: 2,
    },
  ];

  const authorRoleIDORExpression = generateORExpression(orFilters);
  const chatIDExpression = generateEqualExpression('chatID', chatID || '');

  const queryArray = [
    generateCombinedFilterExpressionWithAnd([
      authorRoleIDORExpression,
      chatIDExpression,
    ]),
    generateSortQuery('createdAt', 'Decs'),
    generateIncludeQuery([
      'chatMessageFiles',
      'chatMessageRatingType',
      'chatMessagePluginEvents',
    ]),
    generatePaginationQuery({ pageNumber: pageParam, pageSize }),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.CHAT_MESSAGES, queryArray);
  return fetchWrapper<ChatMessageResponse>({
    url,
    method: 'GET',
  });
};

export const useInfiniteChatMessages = (
  chatID: string | undefined,
  options: PaginationOptions = { pageSize: 10, initialPage: 1 }
) => {
  const { pageSize, initialPage = 1 } = options;

  const queryResult = useInfiniteQuery(
    [SPARK_RESOURCE_TYPES.CHAT_MESSAGES, chatID, 'paginated', pageSize],
    ({ pageParam = initialPage }) =>
      getChatMessagesPage(chatID, pageParam, pageSize),
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalItems = lastPage?.meta?.total || 0;
        const loadedItems = pageSize * allPages.length;

        if (loadedItems >= totalItems) {
          return undefined;
        }

        return allPages.length + 1;
      },
      onError: (error) => {
        if (error instanceof Error) {
          toast.error(`Failed to get chat messages: ${error.message}`);
        }
      },

      ...CACHE_AND_STALE_TIME,
      enabled: Boolean(chatID),
    }
  );

  const allChatMessages =
    queryResult.data?.pages.flatMap((page) => page?.data || []) || [];

  const sortedChatMessages = allChatMessages.sort(
    (a, b) =>
      new Date(b.attributes.createdAt || '').getTime() -
      new Date(a.attributes.createdAt || '').getTime()
  );

  const hasNextPage = queryResult.hasNextPage || false;

  const loadMoreMessages = () => {
    if (hasNextPage) {
      queryResult.fetchNextPage();
    }
  };

  return {
    chatMessages: sortedChatMessages,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    hasNextPage,
    loadMoreMessages,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
};
